import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import GdprConsentBlock from "../components/GdprConsentBlock";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Music2,
  Scissors,
  Palette,
  Lightbulb,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  MailCheck,
  Loader2,
  Gift,
} from "lucide-react";

/**
 * FreeLanding (route `/gratis`)
 *
 * Landing page dedicata alle Meta Ads della versione gratuita.
 * - Mostra SOLO il valore gratuito: 4 strumenti AI già pronti
 * - NESSUNA menzione del piano Pro / a pagamento
 * - Form mini: solo email + password + consensi
 * - Posta su `/api/auth/register/free` (status pending → l'admin approva)
 *
 * Pattern visivo coerente con la dashboard (shadow-brutal, font Satoshi)
 * ma performance-first: nessuna immagine pesante, font già preloaded
 * dall'index.html, contenuto leggero.
 */
const FREE_TOOLS = [
  {
    icon: Music2,
    title: "Genera musica royalty-free per i tuoi Reel",
    body: "Crea brani interamente tuoi con Suno AI. Niente più copyright strike, niente più video silenziati dai social. 10 canzoni gratis al giorno.",
  },
  {
    icon: Scissors,
    title: "Mostra alla cliente 8 tagli diversi dalla sua foto",
    body: "Prompt pronto da incollare su Gemini o ChatGPT. La cliente vede subito come sarebbe con pixie, bob, lob, lunghi, mossi... I tagli più adatti vengono evidenziati.",
  },
  {
    icon: Palette,
    title: "11 colori applicati allo stesso viso, in 1 minuto",
    body: "Aiuti la cliente indecisa a scegliere la tinta giusta mostrandole 11 varianti realistiche. L'AI evidenzia i 2-3 colori più adatti alla sua carnagione.",
  },
  {
    icon: Lightbulb,
    title: "1 idea video al giorno via email",
    body: "Ricevi ogni mattina un'idea pronta da girare nel tuo salone — soggetto, formato, copy. Mai più paura di non sapere cosa pubblicare.",
  },
  {
    icon: Gift,
    title: "Crea offerte promozionali irresistibili in 30 secondi",
    body: "L'AI ti scrive nome offerta, testo promozionale, messaggio WhatsApp e testi per le stories — pronti da copiare. Basta indicare servizio, prezzo e obiettivo.",
  },
];

export default function FreeLanding() {
  const navigate = useNavigate();
  const { registerClientFree, apiError } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);
  const [loading, setLoading] = useState(false);
  // v1.36 double opt-in — swap the form for a "check your email" panel
  // once the user submits successfully. We keep the email around so we
  // can render "Ti abbiamo mandato una mail a x@y" and offer a resend.
  const [pendingEmail, setPendingEmail] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      toast.error("Devi accettare Privacy Policy e Note Legali per registrarti");
      return;
    }
    setLoading(true);
    try {
      const res = await registerClientFree({
        email: form.email.trim(),
        password: form.password,
        consents: {
          terms_accepted: true,
          marketing_accepted: !!acceptedMarketing,
          policy_version: "2026-05",
        },
      });
      setPendingEmail(res.email || form.email.trim());
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="free-landing-page"
      className="min-h-screen bg-[var(--mp-paper)] text-[var(--mp-ink)]"
    >
      {/* === HERO === */}
      <section className="px-5 sm:px-8 pt-10 sm:pt-16 pb-12 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="space-y-5">
            <span
              data-testid="free-landing-badge"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--mp-yellow)] text-[var(--mp-ink)] text-[11px] font-bold uppercase tracking-[0.2em]"
            >
              <Sparkles className="h-3 w-3" />
              100% gratis · Per parrucchieri
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Tutti gli strumenti AI che ti servono per il tuo salone.
              <br />
              <span className="text-[var(--mp-primary)]">Gratis.</span>
            </h1>
            <p className="text-base sm:text-lg text-[var(--mp-muted)] max-w-xl">
              Musica royalty-free per i Reel, prompt pronti per mostrare tagli
              e colori alle clienti, un&apos;idea video al giorno via email. Tutto
              dentro il <strong>Portale Marketing Parrucchieri</strong>.
            </p>

            <div className="flex flex-wrap gap-2 text-[13px]">
              <span className="inline-flex items-center gap-1.5 text-[var(--mp-ink)]">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                Nessuna carta richiesta
              </span>
              <span className="inline-flex items-center gap-1.5 text-[var(--mp-ink)]">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                Iscrizione in 30 secondi
              </span>
              <span className="inline-flex items-center gap-1.5 text-[var(--mp-ink)]">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                Niente spam
              </span>
            </div>
          </div>

          {/* === FORM MINI === */}
          <div
            data-testid="free-landing-form-wrap"
            className="rounded-3xl border-2 border-[var(--mp-ink)] bg-white p-6 sm:p-8 shadow-brutal"
          >
            <h2 className="font-display text-2xl tracking-tight">
              Crea il tuo account gratuito
            </h2>
            <p className="text-[13px] text-[var(--mp-muted)] mt-1 mb-5">
              Solo email + password. Compili il resto solo se vuoi sbloccare
              gli strumenti avanzati, quando vuoi.
            </p>

            {pendingEmail ? (
              <CheckYourEmailPanel email={pendingEmail} />
            ) : (
            <form
              onSubmit={onSubmit}
              data-testid="free-landing-form"
              className="space-y-3.5"
            >
              <div>
                <Label
                  htmlFor="free-email"
                  className="text-[10px] font-bold uppercase tracking-[0.2em]"
                >
                  Email
                </Label>
                <Input
                  id="free-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="free-landing-email"
                  placeholder="tu@email.com"
                  className="mt-1.5 h-12 rounded-xl border-[var(--mp-ink)]"
                />
              </div>
              <div>
                <Label
                  htmlFor="free-pwd"
                  className="text-[10px] font-bold uppercase tracking-[0.2em]"
                >
                  Password
                </Label>
                <Input
                  id="free-pwd"
                  type="password"
                  required
                  minLength={8}
                  pattern="^(?=.*[A-Za-z])(?=.*\d).{8,}$"
                  title="Almeno 8 caratteri, con lettere e numeri"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={update("password")}
                  data-testid="free-landing-password"
                  placeholder="minimo 8 caratteri, lettere e numeri"
                  className="mt-1.5 h-12 rounded-xl border-[var(--mp-ink)]"
                />
              </div>
              <GdprConsentBlock
                acceptedTerms={acceptedTerms}
                setAcceptedTerms={setAcceptedTerms}
                acceptedMarketing={acceptedMarketing}
                setAcceptedMarketing={setAcceptedMarketing}
                testidPrefix="free-landing"
              />
              <Button
                type="submit"
                disabled={loading}
                data-testid="free-landing-submit"
                className="w-full h-12 rounded-xl bg-[var(--mp-ink)] hover:bg-black text-white text-base font-semibold"
              >
                {loading ? "Creazione in corso…" : "Inizia gratis"}
                {!loading && <ArrowRight className="h-4 w-4 ml-2" />}
              </Button>
              <p className="text-[11px] text-[var(--mp-muted)] text-center">
                Hai già un account?{" "}
                <a
                  href="/cliente/login"
                  data-testid="free-landing-login-link"
                  className="font-bold text-[var(--mp-ink)] underline"
                >
                  Accedi qui
                </a>
              </p>
            </form>
            )}
          </div>
        </div>
      </section>

      {/* === COSA OTTIENI === */}
      <section className="px-5 sm:px-8 py-12 bg-[var(--mp-dash-bg)] border-y border-[var(--mp-border)]">
        <div className="max-w-6xl mx-auto space-y-8">
          <div className="text-center">
            <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)]">
              Cosa ottieni subito · 5 strumenti AI
            </div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight mt-2">
              Cinque super-strumenti, pronti dal primo login.
            </h2>
            <p className="text-sm text-[var(--mp-muted)] max-w-2xl mx-auto mt-3">
              Niente da configurare, niente da imparare. Apri, copia il
              prompt, e in 1 minuto hai un contenuto wow.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {FREE_TOOLS.map((t, i) => {
              const Icon = t.icon;
              const isLastOdd =
                i === FREE_TOOLS.length - 1 && FREE_TOOLS.length % 2 === 1;
              return (
                <div
                  key={i}
                  data-testid={`free-landing-tool-${i}`}
                  className={`rounded-3xl border border-[var(--mp-ink)] bg-white p-5 sm:p-6 shadow-brutal-sm flex gap-4 ${
                    isLastOdd ? "sm:col-span-2" : ""
                  }`}
                >
                  <div className="h-12 w-12 rounded-2xl bg-[var(--mp-yellow)] grid place-items-center shrink-0">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-xl tracking-tight leading-tight">
                      {t.title}
                    </div>
                    <p className="mt-2 text-[13px] text-[var(--mp-muted)] leading-snug">
                      {t.body}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center pt-2">
            <a
              href="#top"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              data-testid="free-landing-cta-bottom"
              className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[var(--mp-ink)] text-white text-base font-semibold hover:bg-black transition-colors no-underline"
            >
              Inizia gratis ora
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* === FAQ MINI === */}
      <section className="px-5 sm:px-8 py-12 max-w-3xl mx-auto">
        <h2 className="font-display text-2xl sm:text-3xl tracking-tight mb-6">
          Domande veloci
        </h2>
        <div className="space-y-4 text-[14px] leading-relaxed">
          <div data-testid="free-landing-faq-1">
            <strong>È davvero gratis?</strong>
            <p className="text-[var(--mp-muted)]">
              Sì. Tutti gli strumenti elencati qui sono accessibili senza
              pagare nulla, senza carta di credito. Suno e Gemini hanno
              quote gratis generose ogni giorno.
            </p>
          </div>
          <div data-testid="free-landing-faq-2">
            <strong>Quanto ci metto a iniziare?</strong>
            <p className="text-[var(--mp-muted)]">
              30 secondi: inserisci email e password qui sopra. Riceverai
              accesso appena lo staff approva la tua iscrizione (di solito
              entro 24 ore).
            </p>
          </div>
          <div data-testid="free-landing-faq-3">
            <strong>Cos&apos;è Marketing Parrucchieri SRL?</strong>
            <p className="text-[var(--mp-muted)]">
              La prima agenzia di marketing online specializzata
              esclusivamente nel settore della bellezza e dei parrucchieri.
              Costruiamo strumenti pensati apposta per te.
            </p>
          </div>
        </div>
      </section>

      <footer className="px-5 sm:px-8 py-8 border-t border-[var(--mp-border)] text-center text-[12px] text-[var(--mp-muted)]">
        © {new Date().getFullYear()} Marketing Parrucchieri SRL · P.IVA / CF su
        richiesta · <a href="/privacy-policy" className="underline">Privacy</a> ·{" "}
        <a href="/note-legali" className="underline">Termini</a>
      </footer>
    </main>
  );
}

/**
 * Success state shown after /gratis submission. Keeps the user on the
 * marketing page (higher chance they read the "cosa ottieni" section
 * while checking their inbox) instead of throwing them into a locked
 * dashboard they can't use yet.
 */
function CheckYourEmailPanel({ email }) {
  const { resendVerification, apiError } = useAuth();
  const [resending, setResending] = useState(false);
  const [sentAgain, setSentAgain] = useState(false);
  const resend = async () => {
    setResending(true);
    try {
      await resendVerification(email);
      setSentAgain(true);
      toast.success("Ti abbiamo rimandato il link");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setResending(false);
    }
  };
  return (
    <div
      data-testid="free-landing-check-email"
      className="rounded-2xl border-2 border-[var(--mp-ink)] bg-[var(--mp-yellow)]/40 p-5 text-center"
    >
      <div className="h-12 w-12 mx-auto rounded-full bg-[var(--mp-ink)] grid place-items-center">
        <MailCheck className="h-6 w-6 text-white" />
      </div>
      <h3 className="font-display text-2xl mt-3 leading-tight">
        Controlla la tua email
      </h3>
      <p className="text-sm text-[var(--mp-ink)] mt-2 leading-relaxed">
        Ti abbiamo mandato un link a{" "}
        <strong data-testid="free-landing-check-email-address">{email}</strong>.
        Clicca il link entro <strong>24 ore</strong> per attivare il tuo account
        e sbloccare i 4 strumenti AI.
      </p>
      <p className="text-xs text-[var(--mp-muted)] mt-3">
        Se non lo trovi, controlla anche <strong>spam</strong> o{" "}
        <strong>promozioni</strong>.
      </p>
      <Button
        type="button"
        onClick={resend}
        disabled={resending || sentAgain}
        data-testid="free-landing-resend"
        className="mt-4 rounded-full bg-[var(--mp-ink)] hover:bg-black text-white disabled:opacity-60"
      >
        {resending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : sentAgain ? (
          <span className="inline-flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" /> Rimandato
          </span>
        ) : (
          "Non ho ricevuto niente, rimandalo"
        )}
      </Button>
    </div>
  );
}

